import { ChangeEvent, FC } from 'react';
import { MdCheckBoxOutlineBlank as CheckBoxOutlineBlankIcon } from 'react-icons/md';
import { MdCheckBox as CheckBoxIcon } from 'react-icons/md';
import Checkbox from '@mui/material/Checkbox';
import { makeStyles } from 'tss-react/mui';
import { FormControlLabel, FormGroup, Theme, Typography } from '@mui/material';
import { AppOwnerDefault } from './AppOwnerSelector';

const icon = <CheckBoxOutlineBlankIcon fontSize='small' />;
const checkedIcon = <CheckBoxIcon fontSize='small' />;

const AppOwnerList: FC<React.PropsWithChildren<AppOwnerListProps>> = ({
  entireOrg,
  onOptionSelect,
  options,
  filter,
}) => {
  const { classes } = useStyles();
  return (
    <div role='row' className={classes.appOwnerList}>
      <FormGroup>
        <FormControlLabel
          className={classes.checkboxItem}
          disableTypography
          control={
            <Checkbox
              disabled={options.length === 0}
              onChange={onOptionSelect}
              checked={entireOrg}
              value={AppOwnerDefault.EntireOrganization}
              icon={icon}
              checkedIcon={checkedIcon}
            />
          }
          label={
            <Typography
              lineHeight={2}
              className={classes.text}
              color={entireOrg ? 'primary' : 'text.primary'}
              variant='caption'>
              {AppOwnerDefault.EntireOrganization}
            </Typography>
          }
        />
        {(!!filter
          ? options.filter(option => option.name.toLowerCase().includes(filter))
          : options
        ).map((option, index) => (
          <FormControlLabel
            className={classes.checkboxItem}
            disableTypography
            key={index}
            control={
              <Checkbox
                onChange={onOptionSelect}
                checked={option.checked}
                value={option.email}
                icon={icon}
                checkedIcon={checkedIcon}
              />
            }
            label={
              <Typography
                lineHeight={2}
                className={classes.textOverflow}
                variant='caption'
                color={option.checked ? 'primary' : 'text.primary'}>
                {option.name}
              </Typography>
            }
          />
        ))}
      </FormGroup>
    </div>
  );
};

const useStyles = makeStyles()((theme: Theme) => ({
  textOverflow: {
    maxWidth: 120,
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    display: 'inline-block',
    whiteSpace: 'nowrap',
    flexGrow: 1,
  },
  text: {
    textAlign: 'center',
    textTransform: 'capitalize',
    display: 'flex',
    alignItems: 'center',
  },
  selectedOwners: {
    cursor: 'pointer',
    maxHeight: 38,
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(0.5),
    borderRadius: theme.shape.borderRadius,
    paddingBlock: theme.spacing(1),
    paddingInline: theme.spacing(1),
    border: `1px solid ${theme.palette.text.secondary}`,
  },
  appOwnerList: {
    display: 'flex',
    flexDirection: 'column',
  },
  checkboxItem: {
    margin: '0 !important',
    '&:hover': {
      background:
        theme.palette.mode === 'dark'
          ? 'rgba(255, 255, 255, 0.1)'
          : 'rgba(0, 0, 0, 0.04)',
    },
  },
}));

export interface AppOwnerListProps {
  entireOrg: boolean;
  filter: string;
  options: {
    email: string;
    name: string;
    checked: boolean;
  }[];
  onOptionSelect: (
    event: ChangeEvent<HTMLInputElement>,
    checked: boolean,
  ) => void;
}

export default AppOwnerList;
